import { useState } from "react";

import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { getApiErrorMessage } from "@/api/client";
import { Icon } from "@/components/icons/Icon";
import { Dropdown } from "@/components/sidebar/Dropdown";
import { useStore } from "@/store";

import { RemovePermissionButton } from "./RemovePermissionButton";

import type { DocumentPermission } from "@/api";
import type { DropdownItem } from "@/components/sidebar/Dropdown";

export const EmailPermissionDropdown = observer(function EmailPermissionDropdown({
  permission,
  onError,
}: {
  permission: DocumentPermission;
  onError?: (error: string) => void;
}) {
  const t = useTranslations();
  const { doc } = useStore();
  const [showSpinner, setShowSpinner] = useState(false);
  const [showCheckIcon, setShowCheckIcon] = useState(false);

  if (!doc) {
    return null;
  }

  if (showSpinner || showCheckIcon) {
    return (
      <div className="flex h-9.5">
        {showSpinner ? (
          <Icon name="Spinner" className="m-auto animate-spin" />
        ) : (
          <Icon name="Check" className="m-auto" />
        )}
      </div>
    );
  }

  const items: DropdownItem[] = [
    {
      title: t("share.resend"),
      onClick: async () => {
        setShowSpinner(true);
        try {
          await doc.permissions.resendEmailPermission(permission.uid);
          setShowCheckIcon(true);
          setTimeout(() => setShowCheckIcon(false), 1000);
        } catch (err) {
          onError?.(getApiErrorMessage(err));
        }
        setShowSpinner(false);
      },
    },
    {
      component: ({ close }) => (
        <RemovePermissionButton
          onClick={async () => {
            try {
              await doc.permissions.deletePermission(permission.uid);
              close();
            } catch (err) {
              onError?.(getApiErrorMessage(err));
            }
          }}
        />
      ),
    },
  ];

  const toggleText = t("share.invited");

  return (
    <Dropdown
      anchor="bottom end"
      toggleText={toggleText}
      toggleTextColor="text-document-text"
      toggleTextInvertedBg
      items={items}
      itemsClassName="min-w-[180px]"
    />
  );
});
